import {
  Box,
  Button,
  Card,
  Heading,
  HStack,
  Image,
  Link,
  List,
  SimpleGrid,
  Stack,
  Text,
  VStack,
} from '@chakra-ui/react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { Check } from 'lucide-react'
import logisticImage from '~/assets/illustrations/logisticIllustration.jpg'
import { cardHover, PageHeader, SectionHeading } from '~/components/shell'

export const Route = createFileRoute('/_app/logistics/')({ component: LogisticsDemo })

function LogisticsDemo() {
  const navigate = useNavigate()

  return (
    <Box maxW="1400px" mx="auto">
      <PageHeader title="Logistics" subtitle="Supply chain and delivery optimization" />

      <VStack align="stretch" gap={10}>
        {/* Customer story */}
        <Card.Root overflow="hidden" boxShadow="2xl">
          <Stack direction={{ base: 'column', md: 'row' }}>
            <Image
              objectFit="cover"
              maxW={{ base: '100%', md: '300px' }}
              src={logisticImage}
              alt="Logistics"
            />
            <Stack flex={1} mb="6">
              <Card.Header>
                <Heading size="lg" color="gray.600">
                  Delivering Competitive Advantage Through Real-Time Supply Chain Visibility
                </Heading>
              </Card.Header>
              <Card.Body>
                <VStack align="flex-start" gap={4}>
                  <Text>
                    HPS faced the challenge every global logistics company knows: delayed shipments
                    were eroding customer trust, inefficient routing was inflating fuel costs, and
                    lack of real-time visibility meant problems were discovered too late to fix.
                  </Text>
                  <Text>
                    By deploying the{' '}
                    <Link
                      href="https://www.hivemq.com/products/hivemq-platform/"
                      target="_blank"
                      rel="noreferrer"
                      color="blue.500"
                      _hover={{ textDecoration: 'underline' }}
                    >
                      HiveMQ Platform
                    </Link>
                    , HPS transformed their global operations with a real-time data infrastructure
                    that connects thousands of vehicles, warehouses, and sensors into a unified
                    command center - enabling proactive decision-making at every stage of delivery.
                  </Text>
                  <Text>
                    HiveMQ reliably handles millions of location updates and sensor readings across
                    intermittent cellular networks worldwide, ensuring continuous visibility from
                    warehouse to doorstep.
                  </Text>
                  <Text fontWeight="medium">Business Impact Delivered:</Text>
                  <List.Root pl={4}>
                    <List.Item>
                      <HStack gap={2}>
                        <Box p={0.5} bg="green.100" borderRadius="full">
                          <Check size={12} color="var(--chakra-colors-green-600)" />
                        </Box>
                        <Text fontSize="sm">
                          <b>20% improvement in on-time delivery</b> through real-time route
                          optimization and proactive delay mitigation
                        </Text>
                      </HStack>
                    </List.Item>
                    <List.Item>
                      <HStack gap={2}>
                        <Box p={0.5} bg="green.100" borderRadius="full">
                          <Check size={12} color="var(--chakra-colors-green-600)" />
                        </Box>
                        <Text fontSize="sm">
                          <b>15% reduction in fuel costs</b> with dynamic routing based on live
                          traffic, weather, and vehicle conditions
                        </Text>
                      </HStack>
                    </List.Item>
                    <List.Item>
                      <HStack gap={2}>
                        <Box p={0.5} bg="green.100" borderRadius="full">
                          <Check size={12} color="var(--chakra-colors-green-600)" />
                        </Box>
                        <Text fontSize="sm">
                          <b>Enhanced customer experience</b> with accurate ETAs, real-time
                          tracking, and proactive delivery notifications
                        </Text>
                      </HStack>
                    </List.Item>
                    <List.Item>
                      <HStack gap={2}>
                        <Box p={0.5} bg="green.100" borderRadius="full">
                          <Check size={12} color="var(--chakra-colors-green-600)" />
                        </Box>
                        <Text fontSize="sm">
                          <b>Reduced cargo losses</b> through continuous condition monitoring,
                          security alerts, and compliance tracking
                        </Text>
                      </HStack>
                    </List.Item>
                  </List.Root>
                </VStack>
              </Card.Body>
            </Stack>
          </Stack>
        </Card.Root>

        {/* Platform journey */}
        <VStack align="stretch" gap={4}>
          <SectionHeading title="The Industrial Data Platform for Agentic AI" />
          <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} gap={6}>
            <Card.Root boxShadow="lg">
              <Card.Header>
                <Heading size="md" color="brand.solid">
                  Connect
                </Heading>
              </Card.Header>
              <Card.Body>
                <Text color="content.secondary">
                  Thousands of vehicles and warehouse sensors streaming location and condition data
                </Text>
              </Card.Body>
            </Card.Root>
            <Card.Root boxShadow="lg">
              <Card.Header>
                <Heading size="md" color="brand.solid">
                  Contextualize
                </Heading>
              </Card.Header>
              <Card.Body>
                <Text color="content.secondary">
                  Shipment data unified into a single operational view across the supply chain
                </Text>
              </Card.Body>
            </Card.Root>
            <Card.Root boxShadow="lg">
              <Card.Header>
                <Heading size="md" color="brand.solid">
                  Analyze
                </Heading>
              </Card.Header>
              <Card.Body>
                <Text color="content.secondary">
                  Routes, conditions, and delivery performance analyzed in real time
                </Text>
              </Card.Body>
            </Card.Root>
            <Card.Root boxShadow="lg">
              <Card.Header>
                <Heading size="md" color="brand.solid">
                  Act
                </Heading>
              </Card.Header>
              <Card.Body>
                <Text color="content.secondary">
                  Delay signals trigger proactive rerouting before customers are impacted
                </Text>
              </Card.Body>
            </Card.Root>
          </SimpleGrid>
        </VStack>

        {/* Data flow, UNS and AI */}
        <VStack align="stretch" gap={4}>
          <SectionHeading title="Data Flow, UNS and AI" />
          <SimpleGrid columns={{ base: 1, sm: 2, md: 3, lg: 4 }} gap={6}>
            <Card.Root
              overflow="hidden"
              transition="all 0.3s"
              _hover={cardHover}
              cursor="pointer"
              onClick={() => navigate({ to: '/logistics/flow' })}
              h="100%"
            >
              <Card.Header>
                <Heading size="md">Data Flow</Heading>
              </Card.Header>
              <Card.Body>
                <Text color="content.secondary">Explore the logistics data flow architecture.</Text>
              </Card.Body>
              <Card.Footer>
                <Button
                  w="100%"
                  onClick={(e) => {
                    e.stopPropagation()
                    navigate({ to: '/logistics/flow' })
                  }}
                >
                  View Flow
                </Button>
              </Card.Footer>
            </Card.Root>

            <Card.Root
              overflow="hidden"
              transition="all 0.3s"
              _hover={cardHover}
              cursor="pointer"
              onClick={() => navigate({ to: '/logistics/explorer' })}
              h="100%"
            >
              <Card.Header>
                <Heading size="md">UNS Explorer</Heading>
              </Card.Header>
              <Card.Body>
                <Text color="content.secondary">Explore the Unified Namespace in real-time.</Text>
              </Card.Body>
              <Card.Footer>
                <Button
                  w="100%"
                  onClick={(e) => {
                    e.stopPropagation()
                    navigate({ to: '/logistics/explorer' })
                  }}
                >
                  Explore UNS
                </Button>
              </Card.Footer>
            </Card.Root>

            <Card.Root
              overflow="hidden"
              transition="all 0.3s"
              _hover={cardHover}
              cursor="pointer"
              onClick={() => navigate({ to: '/logistics/bot' })}
              h="100%"
            >
              <Card.Header>
                <Heading size="md">AI Assistant</Heading>
              </Card.Header>
              <Card.Body>
                <Text color="content.secondary">Chat with AI about logistics operations.</Text>
              </Card.Body>
              <Card.Footer>
                <Button
                  w="100%"
                  onClick={(e) => {
                    e.stopPropagation()
                    navigate({ to: '/logistics/bot' })
                  }}
                >
                  Chat with AI
                </Button>
              </Card.Footer>
            </Card.Root>

            <Card.Root
              overflow="hidden"
              transition="all 0.3s"
              _hover={cardHover}
              cursor="pointer"
              onClick={() => navigate({ to: '/logistics/knowledge-graph' })}
              h="100%"
            >
              <Card.Header>
                <Heading size="md">Knowledge Graph</Heading>
              </Card.Header>
              <Card.Body>
                <Text color="content.secondary">
                  Visualize the logistics UNS as a real-time network graph.
                </Text>
              </Card.Body>
              <Card.Footer>
                <Button
                  w="100%"
                  onClick={(e) => {
                    e.stopPropagation()
                    navigate({ to: '/logistics/knowledge-graph' })
                  }}
                >
                  View Graph
                </Button>
              </Card.Footer>
            </Card.Root>
          </SimpleGrid>
        </VStack>
      </VStack>
    </Box>
  )
}
